import axios from "axios";
import type {
  Analysis,
  AnalysisCreate,
  AnalysisDetail,
  AnalysisFeature,
  AnalysisSimilarCase,
  AuditLogListResponse,
  AuthToken,
  Case,
  CaseCreate,
  CaseStats,
  CaseUpdate,
  ChromatogramPoint,
  DeargenCompoundIdentificationRequest,
  DeargenCompoundIdentificationResponse,
  DeargenFullAnalysisRequest,
  DeargenFullAnalysisResponse,
  DeargenHazardAssessmentRequest,
  DeargenHazardAssessmentResponse,
  DeargenHealthResponse,
  DeargenModelInfo,
  EvidenceFileKind,
  EvidenceFilesResponse,
  FileUploadResponse,
  FireExperiment,
  FireExperimentUpsert,
  FireTestListParams,
  FireTestListResponse,
  HealthResponse,
  LibraryMatchItem,
  LoginCredentials,
  MassSpectrumData,
  MzRtPoint,
  RegisterData,
  Report,
  ReportCreate,
  Sample,
  SampleCreate,
  SampleDetail,
  SampleUpdate,
  User,
} from "@/types";
import type {
  AnyPeaksSearchRequest,
  AnyPeaksSearchResponse,
} from "@/types/referenceCompound";
import type {
  ReferenceDBAsset,
  ReferenceDBAssetCreate,
  ReferenceDBAssetListResponse,
} from "@/types/referenceDBAsset";
import type {
  SpectrumComparisonData,
  SpectrumHitsData,
} from "@/types/spectrumComparison";

const api = axios.create({
  baseURL: "/api/v1",
  headers: { "Content-Type": "application/json" },
});

// JWT request interceptor
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 세션 종료 — 토큰/유저 정리 후 로그인 화면으로
function clearSessionAndRedirect() {
  localStorage.removeItem("token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("user");
  window.location.href = "/login";
}

// single-flight refresh — 동시에 여러 요청이 401이어도 refresh는 1회만 수행한다.
// 진행 중인 refresh를 공유 Promise로 들고, 401난 모든 요청이 이를 await한다.
let refreshPromise: Promise<string> | null = null;

function runRefresh(refreshToken: string): Promise<string> {
  if (!refreshPromise) {
    // 인터셉터 재귀를 피하려 raw axios 사용. backend는 refresh token을 Bearer로 받음.
    refreshPromise = axios
      .post("/api/v1/auth/refresh", null, {
        headers: { Authorization: `Bearer ${refreshToken}` },
      })
      .then((resp) => {
        const newAccess = resp.data.access_token as string;
        const newRefresh = resp.data.refresh_token as string | undefined;
        localStorage.setItem("token", newAccess);
        if (newRefresh) localStorage.setItem("refresh_token", newRefresh);
        return newAccess;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

// 401 response interceptor — access(30분) 만료 시 refresh(7일)로 새 토큰을 발급해
// localStorage에 저장한 뒤, 원요청은 reject한다. 실제 재요청은 호출 측(React Query
// retry)이 새 토큰으로 자동 수행한다.
//
// 참고: axios 응답 인터셉터의 onRejected가 "resolve 값"을 반환해도 일부 설정에서
// 호출 측에 그 응답이 전달되지 않고 config 객체가 새는 문제가 있었다. 반면 reject는
// 정확히 전파되므로, "refresh 후 reject → 상위 retry"가 가장 견고하다.
// (refresh가 없거나 실패할 때만 로그아웃 — access 만료마다 끊기던 문제 해결)
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (error.response?.status !== 401 || !original || original._retry) {
      return Promise.reject(error);
    }
    // refresh endpoint 자체의 401은 재귀 방지를 위해 그대로 실패 처리
    if (
      typeof original.url === "string" &&
      original.url.includes("/auth/refresh")
    ) {
      return Promise.reject(error);
    }

    const refreshToken = localStorage.getItem("refresh_token");
    if (!refreshToken) {
      clearSessionAndRedirect();
      return Promise.reject(error);
    }

    // 같은 요청이 무한 refresh 루프에 빠지지 않도록 표시(상위 retry는 새 config로 옴).
    original._retry = true;
    try {
      // 진행 중인 refresh가 있으면 공유, 없으면 시작 — 동시 401도 1회만 갱신.
      await runRefresh(refreshToken);
      // 새 토큰이 localStorage에 저장됨 → 상위(React Query retry)가 새 토큰으로 재요청.
      return Promise.reject(error);
    } catch (refreshError) {
      // refresh 만료(7일 경과)/무효 — 그때만 로그아웃
      clearSessionAndRedirect();
      return Promise.reject(refreshError);
    }
  },
);

// Auth API
export const authApi = {
  login: (credentials: LoginCredentials) =>
    api.post<AuthToken>(
      "/auth/login",
      new URLSearchParams({
        username: credentials.username,
        password: credentials.password,
      }),
      { headers: { "Content-Type": "application/x-www-form-urlencoded" } },
    ),
  register: (data: RegisterData) => api.post<User>("/auth/register", data),
  getMe: () => api.get<User>("/auth/me"),
  changePassword: (data: { current_password: string; new_password: string }) =>
    api.post<{ message: string }>("/auth/change-password", data),
};

// Cases API
export const casesApi = {
  list: (params?: { skip?: number; limit?: number }) =>
    api.get<Case[]>("/cases", { params }),
  get: (id: number) => api.get<Case>(`/cases/${id}`),
  create: (data: CaseCreate) => api.post<Case>("/cases", data),
  update: (id: number, data: CaseUpdate) =>
    api.patch<Case>(`/cases/${id}`, data),
  delete: (id: number) => api.delete(`/cases/${id}`),
  search: (query: string) =>
    api.get<Case[]>("/cases/search/query", { params: { q: query } }),
  getStats: () => api.get<CaseStats>("/cases/stats/overview"),
};

// Samples API
export const samplesApi = {
  list: (params?: {
    case_id?: number;
    skip?: number;
    limit?: number;
    substrate?: string;
    ignitable_liquid?: string;
    evaporation_level?: number;
  }) => api.get<Sample[]>("/samples", { params }),
  get: (id: number) => api.get<Sample>(`/samples/${id}`),
  getDetail: (id: number) => api.get<SampleDetail>(`/samples/${id}/detail`),
  // 모의현장 DB (PLAN 2-11) — 화재시험 조건이 입력된 시료
  listFireTests: (params?: FireTestListParams) =>
    api.get<FireTestListResponse>("/samples/fire-tests", { params }),
  getFireTestFacets: () =>
    api.get<FireTestListResponse["facets"]>("/samples/fire-tests/facets"),
  getFireExperiment: (id: number) =>
    api.get<FireExperiment | null>(`/samples/${id}/fire-experiment`),
  upsertFireExperiment: (id: number, data: FireExperimentUpsert) =>
    api.put<FireExperiment>(`/samples/${id}/fire-experiment`, data),
  deleteFireExperiment: (id: number) =>
    api.delete(`/samples/${id}/fire-experiment`),
  create: (data: SampleCreate) => api.post<Sample>("/samples", data),
  update: (id: number, data: SampleUpdate) =>
    api.patch<Sample>(`/samples/${id}`, data),
  delete: (id: number) => api.delete(`/samples/${id}`),
  upload: (id: number, file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post(`/samples/${id}/upload`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  uploadFiles: (id: number, ticFile?: File, msFile?: File) => {
    const formData = new FormData();
    if (ticFile) formData.append("tic_file", ticFile);
    if (msFile) formData.append("ms_file", msFile);
    return api.post<FileUploadResponse>(
      `/samples/${id}/upload-files`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      },
    );
  },
  importSpectrum: (id: number, data: unknown) =>
    api.post(`/samples/${id}/import-spectrum`, data),

  /**
   * 증거 원본의 상태만 조회한다 (내용 없음, 감사 행 없음).
   * 화면이 "내려받기" 버튼을 정직하게 그리려면 누르기 전에 알아야 한다.
   */
  evidenceFiles: (id: number) =>
    api.get<EvidenceFilesResponse>(`/samples/${id}/evidence-files`),

  /**
   * 증거 원본 바이트를 받는다. **감사 VIEW 행이 남는다** — 성공도, 소실
   * 시도도. 상태를 떠보려고 이걸 부르지 말 것(그러면 감사 이력이 날조된다).
   * 상태 확인은 `evidenceFiles` 로 한다.
   */
  downloadSpectrumFile: (id: number, kind: EvidenceFileKind) =>
    api.get(`/samples/${id}/spectrum-file/${kind}`, { responseType: "blob" }),
};

// Spectrum Data API
export const spectrumApi = {
  getTICData: (sampleId: number) =>
    api.get<{ tic_data: ChromatogramPoint[] }>(`/samples/${sampleId}/tic-data`),
  getMZRTData: (sampleId: number, analysisId?: number, maxPoints?: number) =>
    api.get<{ ms_data: MzRtPoint[] }>(`/samples/${sampleId}/mzrt-data`, {
      params: {
        ...(analysisId !== undefined ? { analysis_id: analysisId } : {}),
        ...(maxPoints !== undefined ? { max_points: maxPoints } : {}),
      },
    }),
  getMassSpectrum: (sampleId: number, rt?: number) =>
    api.get<{ spectrum: MassSpectrumData[] }>(
      `/samples/${sampleId}/mass-spectrum`,
      { params: rt !== undefined ? { rt } : undefined },
    ),
  /** T3-2a: 추출이온 프로파일 — 그리기 위한 데이터만, 판정 없음. */
  getEIP: (sampleId: number, ions?: string) =>
    api.get<import("@/types").EIPResponse>(`/samples/${sampleId}/eip`, {
      params: ions ? { ions } : undefined,
    }),
  getLibraryMatches: (sampleId: number) =>
    api.get<{ matches: LibraryMatchItem[] }>(
      `/samples/${sampleId}/library-matches`,
    ),
};

export const anyPeaksApi = {
  search: (request: AnyPeaksSearchRequest) =>
    api.post<AnyPeaksSearchResponse>(
      "/reference-compounds/any-peaks-search",
      request,
    ),
};

/** 표준 라이브러리 포함 검색 — 브라우저 좌측 목록의 '포함 검색' 모드. */
export const standardSearchApi = {
  search: (q: string, limit = 50) =>
    api.get<import("@/types/referenceCompound").StandardSearchResponse>(
      "/reference-compounds/standard-search",
      { params: { q, limit } },
    ),
};

/** 표준 라이브러리 브라우즈 — 원본 Names 화면의 상시 목록 대응 (UI 재검토 2026-08-20). */
export const standardListApi = {
  list: (params?: {
    from_name?: string;
    after_name?: string;
    after_rowid?: number;
    limit?: number;
  }) =>
    api.get<import("@/types/referenceCompound").StandardListResponse>(
      "/reference-compounds/standard-list",
      { params },
    ),
  /**
   * 표준 라이브러리 물질의 MS 스펙트럼 (T6-3, 2026-08-27). 스펙트럼이 없는
   * 물질(또는 구 sidecar)은 404 — 호출부가 "없음"과 다른 오류를 구분한다.
   */
  spectra: (rowid: number, limit?: number) =>
    api.get<import("@/types/referenceCompound").StandardSpectraResponse>(
      `/reference-compounds/standard-spectra/${rowid}`,
      { params: limit !== undefined ? { limit } : undefined },
    ),
};

// Analyses API
export const analysesApi = {
  list: (params?: { skip?: number; limit?: number; sample_id?: number }) =>
    api.get<Analysis[]>("/analyses", { params }),
  get: (id: number) => api.get<Analysis>(`/analyses/${id}`),
  getDetail: (id: number) => api.get<AnalysisDetail>(`/analyses/${id}/detail`),
  create: (data: AnalysisCreate) => api.post<Analysis>("/analyses", data),
  delete: (id: number) => api.delete(`/analyses/${id}`),
  getFeatures: (id: number) =>
    api.get<{ features: AnalysisFeature[] }>(`/analyses/${id}/features`),
  getSimilarCases: (id: number) =>
    api.get<{ similar_cases: AnalysisSimilarCase[] }>(
      `/analyses/${id}/similar-cases`,
    ),
  getSpectrumComparison: (
    id: number,
    resultId: number,
    compoundId?: number,
    mzTolerance?: number,
  ) =>
    api.get<SpectrumComparisonData>(`/analyses/${id}/spectrum-comparison`, {
      params: {
        result_id: resultId,
        ...(compoundId !== undefined ? { compound_id: compoundId } : {}),
        ...(mzTolerance !== undefined ? { mz_tolerance: mzTolerance } : {}),
      },
    }),
  /** T2-3: 후보 판정 기록 (append-only — 정정은 새 판정으로 남는다). */
  createAssessment: (
    analysisId: number,
    resultId: number,
    data: { status: string; rationale: string },
  ) =>
    api.post<import("@/types").Assessment>(
      `/analyses/${analysisId}/results/${resultId}/assessments`,
      data,
    ),
  /** T2-3: 판정 목록 — 현재(후보별 최신) + 이력. */
  getAssessments: (analysisId: number) =>
    api.get<import("@/types").AssessmentList>(
      `/analyses/${analysisId}/assessments`,
    ),
  getSpectrumHits: (id: number, resultId: number, preprocessing?: string) =>
    api.get<SpectrumHitsData>(`/analyses/${id}/spectrum-hits`, {
      params: {
        result_id: resultId,
        ...(preprocessing ? { preprocessing } : {}),
      },
    }),
  /** 스캔(RT) 앵커 — 저장 행이 없는 스캔도 참조 전량 순위 열람 (2026-08-31). */
  getSpectrumHitsByScanRt: (
    id: number,
    scanRt: number,
    preprocessing?: string,
  ) =>
    api.get<SpectrumHitsData>(`/analyses/${id}/spectrum-hits`, {
      params: {
        scan_rt: scanRt,
        ...(preprocessing ? { preprocessing } : {}),
      },
    }),
  search: (params?: Record<string, unknown>) =>
    api.get<Analysis[]>("/analyses/search", { params }),
  getByCase: (caseId: number) =>
    api.get<Analysis[]>(`/analyses/by-case/${caseId}`),
  /** T3-2a: IL 클래스 스크리닝 — 조건 충족 계산, 판정 아님. */
  getILClassScreening: (analysisId: number, schemeId?: string) =>
    api.get<import("@/types").ILClassScreening>(
      `/analyses/${analysisId}/il-class-screening`,
      { params: schemeId ? { scheme_id: schemeId } : undefined },
    ),
  /** T3-3: 클래스 판정 기록 (append-only — 정정은 새 판정으로 남는다). */
  createClassAssessment: (
    analysisId: number,
    data: {
      scheme_id: string;
      class_key: string;
      status: string;
      rationale: string;
    },
  ) =>
    api.post<import("@/types").ClassAssessment>(
      `/analyses/${analysisId}/class-assessments`,
      data,
    ),
  /** T3-3: 클래스 판정 목록 — 현재((스키마, 클래스)별 최신) + 이력. */
  getClassAssessments: (analysisId: number) =>
    api.get<import("@/types").ClassAssessmentList>(
      `/analyses/${analysisId}/class-assessments`,
    ),
};

// Reports API
export const reportsApi = {
  list: (params?: { skip?: number; limit?: number }) =>
    api.get<Report[]>("/reports", { params }),
  get: (id: number) => api.get<Report>(`/reports/${id}`),
  create: (data: ReportCreate) => api.post<Report>("/reports", data),
  download: (id: number) =>
    api.get(`/reports/${id}/download`, { responseType: "blob" }),
  delete: (id: number) => api.delete(`/reports/${id}`),
  downloadAnalysisPptx: (analysisId: number) =>
    api.get(`/reports/analysis/${analysisId}/pptx`, { responseType: "blob" }),
  downloadCasePptx: (caseId: number) =>
    api.get(`/reports/case/${caseId}/pptx`, { responseType: "blob" }),
};

// Health API
export const healthApi = {
  check: () => api.get<HealthResponse>("/health"),
  checkDB: () => api.get<HealthResponse>("/health/db"),
  checkAI: () => api.get<HealthResponse>("/health/ai"),
};

// Deargen AI API
export const deargenApi = {
  identifyCompounds: (data: DeargenCompoundIdentificationRequest) =>
    api.post<DeargenCompoundIdentificationResponse>(
      "/deargen/identify-compounds",
      data,
    ),
  assessHazards: (data: DeargenHazardAssessmentRequest) =>
    api.post<DeargenHazardAssessmentResponse>("/deargen/assess-hazards", data),
  fullAnalysis: (data: DeargenFullAnalysisRequest) =>
    api.post<DeargenFullAnalysisResponse>("/deargen/full-analysis", data),
  checkHealth: () => api.get<DeargenHealthResponse>("/deargen/health"),
  getModelInfo: () => api.get<DeargenModelInfo>("/deargen/model-info"),
};

// Admin API
export const adminApi = {
  listUsers: (params?: {
    skip?: number;
    limit?: number;
    role?: string;
    is_active?: boolean;
  }) => api.get<User[]>("/admin/users", { params }),
  getUser: (id: number) => api.get<User>("/admin/users/" + id),
  createUser: (data: {
    email: string;
    username: string;
    password: string;
    full_name?: string;
    department?: string;
    role?: string;
    is_active?: boolean;
  }) => api.post<User>("/admin/users", data),
  updateUser: (
    id: number,
    data: {
      email?: string;
      full_name?: string;
      department?: string;
      role?: string;
      is_active?: boolean;
    },
  ) => api.patch<User>("/admin/users/" + id, data),
  approveUser: (id: number) =>
    api.patch<User>("/admin/users/" + id + "/approve"),
  deactivateUser: (id: number) => api.delete("/admin/users/" + id),
};

// Audit Logs API
export const auditLogsApi = {
  list: (params?: {
    entity_type?: string;
    entity_id?: number;
    user_id?: number;
    action?: string;
    date_from?: string;
    date_to?: string;
    skip?: number;
    limit?: number;
  }) => api.get<AuditLogListResponse>("/audit-logs", { params }),
};

// Reference DB Asset API (admin only) — S23-D 증분3
export const referenceDBApi = {
  list: () => api.get<ReferenceDBAssetListResponse>("/admin/reference-db"),
  get: (id: number) => api.get<ReferenceDBAsset>(`/admin/reference-db/${id}`),
  register: (data: ReferenceDBAssetCreate) =>
    api.post<ReferenceDBAsset>("/admin/reference-db", data),
  activate: (id: number) =>
    api.post<ReferenceDBAsset>(`/admin/reference-db/${id}/activate`),
};

export default api;
